/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useOverflow, useMergedRefs, Box } from '../../utils/index.js';
import { Composite, CompositeItem, FloatingDelayGroup, } from '@floating-ui/react';
// ----------------------------------------------------------------------------
/** This context is used for letting descendant IconButtons know the ButtonGroup's orientation. */
export const ButtonGroupContext = React.createContext(undefined);
ButtonGroupContext.displayName = 'ButtonGroupContext';
/**
 * Group buttons together for common actions.
 * Handles responsive overflow when the `overflowButton` prop is specified.
 *
 * @example
 * <ButtonGroup role="toolbar">
 *   <IconButton>
 *     <SvgAdd />
 *   </IconButton>
 *   <IconButton>
 *     <SvgEdit />
 *   </IconButton>
 * </ButtonGroup>
 *
 * @example
 * const buttons = [...Array(10)].map((_, index) => <IconButton><SvgPlaceholder /></IconButton>);
 * <ButtonGroup
 *   overflowButton={(overflowStart) => <DropdownMenu menuItems={(close) =>
 *     [...Array(buttons.length - overflowStart + 1)].map((_, index) => (
 *       <MenuItem icon={<SvgPlaceholder />} onClick={close}>Button #{overflowStart + index}</MenuItem>
 *     ))
 *   }>
 *     <IconButton><SvgMore /></IconButton>
 *   </DropdownMenu>}
 * >
 *   {buttons}
 * </ButtonGroup>
 */
export const ButtonGroup = React.forwardRef((props, forwardedRef) => {
    const { children: childrenProp, overflowButton, overflowPlacement = 'end', orientation = 'horizontal', ...rest } = props;
    const children = React.useMemo(() => {
        if (props.role !== 'toolbar') {
            return childrenProp;
        }
        return React.Children.map(childrenProp, (child, index) => React.isValidElement(child) ? (React.createElement(CompositeItem, { key: index, render: child })) : (child));
    }, [childrenProp, props.role]);
    const node = overflowButton ? (React.createElement(OverflowGroup, { orientation: orientation, overflowButton: overflowButton, overflowPlacement: overflowPlacement, ref: forwardedRef, ...rest }, children)) : (React.createElement(BaseGroup, { orientation: orientation, ref: forwardedRef, ...rest }, children));
    return (React.createElement(FloatingDelayGroup, { delay: { open: 50, close: 250 } },
        React.createElement(ButtonGroupContext.Provider, { value: orientation }, props.role === 'toolbar' ? (React.createElement(Composite, { orientation: orientation, render: node })) : (node))));
});
// ----------------------------------------------------------------------------
const BaseGroup = React.forwardRef((props, forwardedRef) => {
    const { orientation, className, ...rest } = props;
    return (React.createElement(Box, { className: cx('iui-button-group', className), "data-iui-orientation": orientation === 'vertical' ? orientation : undefined, ref: forwardedRef, ...rest }));
});
// ----------------------------------------------------------------------------
const OverflowGroup = React.forwardRef((props, forwardedRef) => {
    const { children: childrenProp, orientation, overflowButton, overflowPlacement, ...rest } = props;
    const items = React.useMemo(() => React.Children.toArray(childrenProp).filter(Boolean), [childrenProp]);
    const [overflowRef, visibleCount] = useOverflow(items, !overflowButton, orientation);
    return (React.createElement(BaseGroup, { orientation: orientation, ...rest, className: cx({
            'iui-button-group-overflow-x': !!overflowButton && orientation === 'horizontal',
        }, props.className), ref: useMergedRefs(forwardedRef, overflowRef) }, (() => {
        if (!(visibleCount < items.length)) {
            return items;
        }
        const overflowStart = overflowPlacement === 'start'
            ? items.length - visibleCount
            : visibleCount - 1;
        return (React.createElement(React.Fragment, null,
            overflowButton &&
                overflowPlacement === 'start' &&
                overflowButton(overflowStart),
            overflowPlacement === 'start'
                ? items.slice(overflowStart + 1)
                : items.slice(0, Math.max(0, overflowStart)),
            overflowButton &&
                overflowPlacement === 'end' &&
                overflowButton(overflowStart)));
    })()));
});
